import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search } from 'lucide-react';
import InfoTooltip from "./InfoTooltip";

// Fast, simple checkbox component
const FastCheckbox = ({ value, label, checked, onChange }) => (
  <label className="flex items-center space-x-2 cursor-pointer py-1 hover:bg-gray-50 px-2 rounded">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(value, e.target.checked)}
      className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
    />
    <span className="text-sm text-gray-700 flex-1">{label}</span>
  </label>
);

// Debounced search hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Searchable filter component
const SearchableFilter = ({ title, options, selected, onChange, description }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAll, setShowAll] = useState(false);
  
  // Debounce search to prevent excessive filtering
  const debouncedSearchTerm = useDebounce(searchTerm, 150);
  
  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return options;
    }
    
    const searchLower = debouncedSearchTerm.toLowerCase();
    return options.filter(option => 
      option.toLowerCase().includes(searchLower)
    );
  }, [options, debouncedSearchTerm]);
  
  // Sort options: selected first, then alphabetical
  const sortedOptions = useMemo(() => {
    return [...filteredOptions].sort((a, b) => {
      const aSelected = selected.includes(a);
      const bSelected = selected.includes(b);
      
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.localeCompare(b);
    });
  }, [filteredOptions, selected]);
  
  // Show only first 50 items unless "show all" is clicked
  const displayOptions = useMemo(() => {
    return showAll ? sortedOptions : sortedOptions.slice(0, 50);
  }, [sortedOptions, showAll]);
  
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setShowAll(false); // Reset show all when searching
  }, []);
  
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setShowAll(false);
  }, []);
  
  const hasMore = sortedOptions.length > 50 && !showAll;
  const hiddenCount = sortedOptions.length - 50;

  return (
    <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
          {options.length} available
        </span>
        {description && <InfoTooltip description={description} />}
      </div>
      
      {/* Search input */}
      <div className="relative mb-3">
        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
          <Search size={14} className="text-gray-400" />
        </div>
        <input
          type="text"
          placeholder={`Search ${title.toLowerCase()}...`}
          value={searchTerm}
          onChange={handleSearchChange}
          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
        />
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        )}
      </div>
      
      {/* Results info */}
      {searchTerm && (
        <div className="text-xs text-gray-500 mb-2">
          {filteredOptions.length === 0 ? (
            `No ${title.toLowerCase()} found`
          ) : (
            `${filteredOptions.length} ${title.toLowerCase()} found`
          )}
        </div>
      )}
      
      {/* Options list */}
      <div className="max-h-48 overflow-y-scroll space-y-1" style={{scrollbarWidth: 'thin'}}>
        {displayOptions.length > 0 ? (
          displayOptions.map((option) => (
            <FastCheckbox
              key={option}
              value={option}
              label={option}
              checked={selected.includes(option)}
              onChange={onChange}
            />
          ))
        ) : (
          <div className="text-sm text-gray-400 italic py-2">
            {searchTerm ? `No ${title.toLowerCase()} match "${searchTerm}"` : `No ${title.toLowerCase()} available`}
          </div>
        )}
        
        {/* Show more button */}
        {hasMore && (
          <button
            onClick={() => setShowAll(true)}
            className="w-full text-xs text-green-600 hover:text-green-700 py-2 border-t border-gray-100 mt-2"
          >
            Show {hiddenCount} more...
          </button>
        )}
      </div>
      
      {/* Selected count */}
      {selected.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            {selected.length} selected
          </span>
        </div>
      )}
    </div>
  );
};

export default SearchableFilter;
